import {
    gridRowIsEditingSelector,
    renderActionsCell,
    useGridApiContext,
    useGridRootProps,
    useGridSelector,
    type GridRenderCellParams
} from '../lib/dataGrid';
import { useIsScrolling } from './useIsScrolling';

/**
 * MUI's own actions cell — but only once the list stands still.
 *
 * An actions column is the most expensive kind of cell a grid has: four or five
 * icon buttons per row, each with its ripple, its tooltip and its SVG. Measured
 * on a project's Befunde list (1422 Befunde, 4x throttled CPU), the column was
 * about half the cost of scrolling — 116 buttons and 116 icons built and torn
 * down again over twenty scroll steps, none of which anybody can click while the
 * list is moving.
 *
 * Nothing changes at rest: this hands over to `renderActionsCell`, the very
 * renderer MUI installs for `type: 'actions'`, so the overflow menu, the
 * keyboard handling and the tooltips are the originals. The cell's box is drawn
 * by the grid from the column width, so the empty moment reserves exactly the
 * same space and nothing shifts.
 *
 * Use it on any actions column of a grid long enough to scroll:
 *
 * ```tsx
 * {
 *     field: 'actions',
 *     type: 'actions',
 *     getActions: …,
 *     renderCell: (params) => <DeferredActionsCell {...params} />
 * }
 * ```
 */
export default function DeferredActionsCell(params: GridRenderCellParams) {
    const scrolling = useIsScrolling();
    const apiRef = useGridApiContext();
    /**
     * A row open for editing keeps its buttons no matter what. In the
     * Zeiterfassung this column carries Speichern and Abbrechen while a row is
     * open — the two ways OUT of that row — and a scroll that took them away
     * would strand it. Asked through the grid's own selector rather than off the
     * api ref, so the cell also re-renders when the row is opened or closed.
     * The grid's OWN edit mode, not a hardcoded 'row': the selector is literally
     * `editMode === Row && Boolean(editRows[rowId])`, so passing 'row' to a grid
     * in MUI's default cell mode would answer "not editing" for every row and
     * quietly retire the guarantee above.
     */
    const { editMode } = useGridRootProps();
    const rowEditing = useGridSelector(apiRef, gridRowIsEditingSelector, {
        rowId: params.id,
        editMode
    });
    if (scrolling && !rowEditing) {
        return null;
    }
    return renderActionsCell(params);
}
